<template>
  <div class="">
    <div class="course px-4 py-3" v-if="!courseDetail">
      <!-- TITLE AND BUTTON ROW -->

      <div class="d-flex align-items-center justify-content-between mb-1">
        <div class="col-8">
          <h3
            class="course-name ml-4 text-decoration-none font-weight-bold fs-2"
          >
            {{ course.course_name }}
          </h3>
        </div>
        <div class="d-none d-lg-block">
          <button
            class="btn btn-secondary-red course-btn mb-3 mb-md-0"
            @click.stop.prevent="redirectApp()"
          >
            Apply
          </button>
        </div>
      </div>

      <!-- COURSE INFO ROW -->
      <div class="d-flex align-items-center flex-wrap" style="gap: 48px">
        <!-- Course Category -->
        <div class="d-flex align-items-center" style="gap: 15px">
          <div class="align-self-center">
            <img class="" src="@/assets/imgs/svgs/cap.svg" alt="" />
          </div>
          <div class="fs-14">
            <p class="font-weight-bold mb-0">Discipline</p>
            <p class="text-secondary font-weight-bold text-truncate" style="font-size: 14px">
              {{ course.course_category_name }}
            </p>
          </div>
        </div>

        <!-- Course Level -->
        <div class="d-flex align-items-center" style="gap: 15px">
          <img src="@/assets/imgs/svgs/star-green.svg" alt="" />
          <div class="align-self-center">
            <p class="font-weight-bold mb-0">Level</p>
            <p class="text-secondary font-weight-bold">
              {{ course.course_level_name }}
            </p>
          </div>
        </div>

        <!-- Fees -->
        <div class="d-flex align-items-center" style="gap: 15px">
          <img src="@/assets/imgs/svgs/money.svg" alt="money" />
          <div class="align-self-center">
            <p class="font-weight-bold mb-0">Fees (P.A.)</p>
            <p
              class="text-secondary font-weight-bold"
              v-if="course.course_fee_pa"
            >
              {{ course.currency }}
              {{ course.course_fee_pa.toLocaleString("en-IN") }}
            </p>
            <p class="font-weight-bold" v-else>
              INR
              {{ course.course_fee.toLocaleString("en-IN") }}
            </p>
          </div>
        </div>

        <!-- Duration -->
        <div class="d-flex align-items-center" style="gap: 15px">
          <img src="@/assets/imgs/svgs/time.svg" alt="" />
          <div class="align-self-center">
            <p class="font-weight-bold mb-0">Duration</p>
            <span class="text-secondary font-weight-bold">
              <p>{{ getCourseDuration(course) }}</p>
            </span>
          </div>
        </div>

        <!-- Intake Months -->
        <div class="d-flex align-items-center" style="gap: 15px">
          <img
            style="height: 20px; margin-top: 3px"
            src="@/assets/imgs/calendar.png"
            alt=""
          />
          <div class="align-self-center">
            <p class="font-weight-bold mb-0">Intakes</p>
            <p class="text-secondary font-weight-bold">
              {{ formatIntake(course.intake) }}
            </p>
          </div>
        </div>
      </div>
      <div class="d-block d-lg-none mt-4">
          <button
            class="btn btn-secondary-red course-btn mb-3 mb-md-0"
            @click.stop.prevent="redirectApp()"
          >
            Apply
          </button>
        </div>
    </div>

    <!-- COURSE DETAILS SECTION -->
    <div class="course-detail bg-primary-1-light-3 p-4 text-left" v-if="courseDetail">
      <!-- APPLICATION FEE -->
      <div v-if="course.application_fee">
        <p class="course-detail__sm-title mb-0">Application Fees</p>
        <div v-html="course.application_fee"></div>
      </div>

      <!-- COURSE BENEFITS -->
      <div v-if="course.course_benefits">
        <div class="course-detail__sm-title">Course Benefits</div>
        <div class="course-detail__text">
          <p v-html="replaceBr(course.course_benefits)"></p>
        </div>
      </div>

      <div class="container-fluid">
        <hr class="mb-2" />
      </div>

      <div class="row">
        <!-- GENERAL ELIGIBILITY -->
        <div v-if="course.general_eligibility" class="mb-2 col-12 col-md-4">
          <div class="course-detail__sm-title mb-1">General Eligibility</div>
          <div>
            {{ course.general_eligibility }}
          </div>
        </div>
        <!-- LANGUAGE REQUIREMENT -->
        <div v-if="course.languages_requirement" class="mb-2 col-12 col-md-4">
          <div class="course-detail__sm-title mb-1">Language Requirement</div>
          <div>
            {{ course.languages_requirement }}
          </div>
        </div>
        <!-- ADDITIONAL INFORMATION -->
        <div class="mb-2 col-12 col-md-4">
          <div class="course-detail__sm-title mb-1">Additional Information</div>
          <div>
            <a :href="course.additional_information_link">{{
              course.additional_information_link
            }}</a>
          </div>
        </div>
        <!-- INTAKES -->
        <div class="mb-2 mb-md-0 col-12 col-md-4" v-if="course.intake">
          <!-- {{ JSON.stringify(course) }} -->
          <div class="course-detail__sm-title mb-1">Intake</div>
          <div>
            <span>Intake Start:</span>
            <span>{{ course.intake }}</span>
          </div>
          <div v-if="course.intake_start">
            <span>Intake Start:</span>
            <span>{{ course.intake_start }}</span>
          </div>
          <div v-if="course.intake_end">
            <span>Intake End:</span>
            <span>{{ course.intake_end }}</span>
          </div>
          <div
            v-if="course.intake_deadline_1 && course.intake_deadline_1 !== '-'"
          >
            <span>Intake Deadline 1:</span>
            <span>{{ course.intake_deadline_1 }}</span>
          </div>
          <div
            v-if="course.intake_deadline_2 && course.intake_deadline_2 !== '-'"
          >
            <span>Intake Deadline 2:</span>
            <span>{{ course.intake_deadline_2 }}</span>
          </div>
          <div
            v-if="course.intake_deadline_3 && course.intake_deadline_3 !== '-'"
          >
            <span>Intake Deadline 3:</span>
            <span>{{ course.intake_deadline_3 }}</span>
          </div>

          <div
            v-if="course.intake_deadline_4 && course.intake_deadline_4 !== '-'"
          >
            <span>Intake Deadline 4:</span>
            <span>{{ course.intake_deadline_4 }}</span>
          </div>
          <div
            v-if="course.intake_deadline_5 && course.intake_deadline_5 !== '-'"
          >
            <span>Intake Deadline 5:</span>
            <span>{{ course.intake_deadline_5 }}</span>
          </div>
          <div
            v-if="course.intake_deadline_6 && course.intake_deadline_6 !== '-'"
          >
            <span>Intake Deadline 6:</span>
            <span>{{ course.intake_deadline_6 }}</span>
          </div>
        </div>
        <!-- INTERNATIONAL SCHOLARSHIP DESCRIPTION -->
        <div class="col-12 col-md-4">
          <div class="course-detail__sm-title mb-1">
            International Scholarship Description
          </div>
          <div>
            <p>{{ course.internation_scholarship_description }}</p>
          </div>
          <div class="fs-14">
            <span class="font-bold">Scholarship Page Link:</span>
            <a href="">{{ course.scholarship_page_link }}</a>
          </div>
          <div class="fs-14">
            <span class="font-bold">Stem Course:</span>
            <span>{{ course.stem_course }}</span>
          </div>
          <div class="fs-14">
            <span class="font-bold">Fifteen Years Education Acceptable:</span>
            <span>{{ course.fifteen_years_education_acceptable }}</span>
          </div>
          <div class="fs-14">
            <span class="font-bold">IELTS Waiver:</span>
            <span>{{ course.ielts_wavier }}</span>
          </div>
          <div class="fs-14">
            <span>{{ course.if_ielts_is_waived_conditions_for }}</span>
          </div>
        </div>
        <!-- GRE -->
        <div class="mb-2 col-12 col-md-4">
          <div class="course-detail__sm-title mb-1">GRE</div>
          <div>
            <span>GRE Requirement:</span>
            <span>{{ course.gre_requirment }}</span>
          </div>
          <div>
            <span>GRE Score:</span>
            <span>{{ course.gre_score }}</span>
          </div>
          <div>
            <span>SAT Requirement:</span>
            <span>{{ course.sat_requirement }}</span>
          </div>
          <div>
            <span>SAT Score:</span>
            <span>{{ course.sat_score }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Course",
  props: {
    course: {
      type: Object,
      default: {},
    },
    courseDetail: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isReadMoreActive: false,
    };
  },
  methods: {
    replaceBr(text) {
      if (text && typeof text === "string") {
        return text.replace(/&lt;br \/&gt;/g, '<br />');
      }
     return text;
    },
    getCourseDuration(course) {
      try {
        if (course.course_year && course.course_month) {
          const y = parseInt(course.course_year);
          const m = parseInt(course.course_month);
          return `${y * 12 + m} Months`;
        } else if (course.course_year) {
          return `${course.course_year} Years`;
        } else if (course.course_month) {
          return `${course.course_month} Months`;
        }
      } catch (err) {
        console.error(err);
        return "";
      }
    },

    formatIntake(intake) {
      if (intake && typeof intake === "string") {
        let split = " ";
        if (intake.includes(",")) {
          split = ",";
        }
        if (intake.includes("/")) {
          split = "/";
        }
        return intake
          .replace(/ /g, "")
          .split(split)
          .map((month) => month.slice(0, 3))
          .join(", ");
      }
      return "";
    },
    redirectApp() {
      window.open("https://app.aliff.in/register", "_blank");
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Josefin+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&family=Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");
.course {
  background-color: var(--primary-1-light-3);
  /* background-color: var(--primary-2-light); */
  /* border: 2px solid var(--primary-1); */
  border: 2px solid var(--primary);
  border-radius: 10px;
  padding: 0.5rem 1rem;
  /* margin-bottom: 2em; */
  font-family: "Montserrat", serif !important;
}

.course .course-name {
  /* color: var(--primary-1); */
  color: var(--primary);
  font-weight: 600;
  text-align: left;
  font-size: 1rem;
  text-decoration: underline;
  font-family: "Montserrat", serif !important;
}

h3.course-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
h3.course-name:hover {
  white-space: normal;
  text-overflow: clip;
}
.course p {
  margin: 0;
}

.course-detail {
  border-block-end: 2px solid var(--primary);
  border-inline-start: 2px solid var(--primary);
  border-inline-end: 2px solid var(--primary);
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  margin-block-start: -12px;
}
.course-detail > div {
  margin-bottom: 10px;
}

.course-detail__sm-title {
  font-weight: bold;
  /* text-decoration: underline; */
  text-transform: uppercase;
}

/* .course-detail__text {
  font-size: 14px;
} */

.course-btn {
  max-width: 100%;
  background: var(--primary) !important;
  color: #fff !important;
}

@media screen and (min-width: 768px) {
  .course-btn {
    max-width: fit-content;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Search/helper/Course.vue